import type { InventoryTrackingMode } from '@/lib/api/types';

/**
 * `InventoryLotStatus` in `types.ts` is widened with `| string` for forward
 * compatibility, which makes it useless as a validator. These are the four the
 * API's Zod enum actually accepts on `?status=` and on `PUT /inventory/lots/:id`.
 */
export type LotStatus = 'active' | 'depleted' | 'expired' | 'disposed';

/**
 * One screen, seven sub-tabs, one search-param bag. (The prototype "Fresh trend" tab was retired by
 * TASK-0259: Reports › Fresh in / out replaced it.)
 *
 * Stock lives in the operator's head as a single subject — "what do we have,
 * what is short, what is about to go off, what did we never classify" — so it
 * is one screen with the tab in the URL, not seven routes that each forget the
 * filters you set on the last one. Every filter, the page, the selected row and
 * the report window are all here so a refresh or a pasted link reproduces the
 * exact view.
 */

export const INVENTORY_TABS = [
  'stock',
  'low',
  'expiring',
  'lots',
  'tracking',
  'import',
  'counts',
] as const;

export type InventoryTab = (typeof INVENTORY_TABS)[number];

export const TAB_LABELS: Record<InventoryTab, string> = {
  stock: 'Stock',
  low: 'Low stock',
  expiring: 'Expiring',
  lots: 'Lots',
  tracking: 'Tracking',
  import: 'Import',
  counts: 'Counts',
};

export interface InventorySearch {
  tab: InventoryTab;
  /** Selected product id — the SplitView inspector on Stock and Tracking. */
  id?: number;
  q: string;
  dept?: number;
  /** Tracking-mode filter, shared by Stock and Tracking. */
  mode?: InventoryTrackingMode;
  sort: string;
  order: 'asc' | 'desc';
  page: number;
  /** Expiring: report window in days. */
  days: number;
  /** Expiring: alert bucket threshold in days. */
  threshold: number;
  lotStatus?: LotStatus;
  lotProduct?: number;
  /** Lots: `expiring_before` as YYYY-MM-DD. */
  before?: string;
  /** Counts: the count sheet open in the history. */
  batch?: number;
  /** Tracking (TASK-0238): items a vendor carries. */
  vendor?: number;
  /** Tracking: sold on any of the last N store days. */
  sold?: number;
  /** Tracking: which items by active state; absent = active items only. */
  active?: 'inactive' | 'all';
}

export interface TabProps {
  search: InventorySearch;
  /** Merge a partial into the URL search params. `undefined` clears a key. */
  patch: (next: Partial<InventorySearch>) => void;
}

const TRACKING_MODES: InventoryTrackingMode[] = ['unclassified', 'tracked', 'untracked'];
export const LOT_STATUSES: LotStatus[] = ['active', 'depleted', 'expired', 'disposed'];

function str(value: unknown): string | undefined {
  return typeof value === 'string' && value !== '' ? value : undefined;
}

function posInt(value: unknown): number | undefined {
  const n = Number(value);
  return Number.isInteger(n) && n > 0 ? n : undefined;
}

/** YYYY-MM-DD only — anything else is dropped rather than sent to the API. */
function isoDay(value: unknown): string | undefined {
  const s = str(value);
  return s && /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : undefined;
}

function oneOf<T extends string>(value: unknown, allowed: readonly T[]): T | undefined {
  const s = str(value);
  return s && (allowed as readonly string[]).includes(s) ? (s as T) : undefined;
}

export function validateInventorySearch(raw: Record<string, unknown>): InventorySearch {
  return {
    tab: oneOf(raw.tab, INVENTORY_TABS) ?? 'stock',
    id: posInt(raw.id),
    q: str(raw.q) ?? '',
    dept: posInt(raw.dept),
    mode: oneOf(raw.mode, TRACKING_MODES),
    sort: str(raw.sort) ?? 'productName',
    order: raw.order === 'desc' ? 'desc' : 'asc',
    page: posInt(raw.page) ?? 1,
    days: posInt(raw.days) ?? 30,
    threshold: posInt(raw.threshold) ?? 21,
    lotStatus: oneOf(raw.lotStatus, LOT_STATUSES),
    lotProduct: posInt(raw.lotProduct),
    before: isoDay(raw.before),
    batch: posInt(raw.batch),
    vendor: posInt(raw.vendor),
    sold: posInt(raw.sold),
    active: oneOf(raw.active, ['inactive', 'all'] as const),
  };
}
